import Vue from 'vue';

type ArbitraryFunction = (...args: any[]) => any;

/**
 * Only call `fn` once per `delay`. Use this for functions that can be called many times,
 * e.g. scroll or resize listeners.
 */
export function throttle(fn: ArbitraryFunction, delay: number): ArbitraryFunction {
  let lastCall = 0;
  return (...args: any[]) => {
    const now = Date.now();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return Vue.nextTick(() => fn(...args));
  };
}

/**
 * Wait for `delay` before calling `fn`. Timer is reset each time the function is called. This is
 * useful for waiting until a user is done typing before calling a function, for example.
 */
export function debounce(fn: ArbitraryFunction, delay: number): ArbitraryFunction {
  let timeout: number;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = window.setTimeout(() => {
      return Vue.nextTick(() => fn(...args));
    }, delay);
  };
}

export function defer(fn: ArbitraryFunction) {
  setTimeout(() => fn(), 1);
}

export function asyncDefer(): Promise<void> {
  return asyncTimeout(1);
}

export function asyncTimeout(n: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(() => resolve(), n);
  });
}
